/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 15/05/2025 17:48
 *
 */

import {
  Component,
  effect,
  ElementRef,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Widget } from '../../../engine/widget/widget';
import { WidgetWrapperComponent } from '../widget-wrapper/widget-wrapper.component';
import {
  ActionEvent,
  CommonUtils,
  StandardEntity,
  SwitchAction,
} from '@infodata-group/inf-commons';
import { TranslateModule } from '@ngx-translate/core';
import { of } from 'rxjs';
import { SwitchActionBuilder } from '../../../engine/builder/switch-action.builder';
import { ToolsActions } from '../../../engine/tools/tools-actions';
import { ComboComponent } from '@infodata-group/inf-commons-desktop';

@Component({
  selector: 'infl-widget-time',
  standalone: true,
  imports: [
    CommonModule,
    WidgetWrapperComponent,
    TranslateModule,
    ComboComponent,
  ],
  templateUrl: './widget-time.component.html',
  styleUrl: './widget-time.component.scss',
})
export class WidgetTimeComponent {
  public readonly actions = [
    new SwitchActionBuilder('customize', 'cogs')
      .setTooltip('i18n.common.customize')
      .build(),
    ToolsActions.getCloseAction(),
  ];

  public widget = input.required<Widget>();

  public time = signal(new Date());

  public displayOptions = signal(false);
  public color = signal(CommonUtils.color('green'));

  protected colors$ = of(this.createColorDatasource());

  @ViewChild('hoursHand') hoursHand: ElementRef<HTMLDivElement>;
  @ViewChild('minutesHand') minutesHand: ElementRef<HTMLDivElement>;
  @ViewChild('secondsHand') secondsHand: ElementRef<HTMLDivElement>;

  constructor() {
    effect(() => {
      const hours = this.time().getHours();
      const minutes = this.time().getMinutes();
      const seconds = this.time().getSeconds();

      this.hoursHand.nativeElement.style.transform = `rotate(${(hours % 12) * 30 + minutes * 0.5}deg)`;
      this.minutesHand.nativeElement.style.transform = `rotate(${minutes * 6}deg)`;
      this.secondsHand.nativeElement.style.transform = `rotate(${seconds * 6}deg)`;
    });

    setInterval(() => {
      this.time.set(new Date());
    }, 500);
  }

  protected onActionClick($event: ActionEvent) {
    switch ($event.action.name) {
      case 'customize':
        this.displayOptions.set(
          ($event.action as SwitchAction<boolean>).isActive,
        );
        break;
      default:
        console.log(`Action ${$event.action.name} not implemented`);
        break;
    }
  }

  private createColorDatasource(): StandardEntity[] {
    const data = [
      { id: CommonUtils.color('green'), name: 'Vert', css: 'success' },
      { id: CommonUtils.color('red'), name: 'Rouge', css: 'danger' },
      { id: CommonUtils.color('link'), name: 'Bleu', css: 'info' },
      { id: CommonUtils.color('primary-color'), name: 'Jaune', css: 'warning' },
    ];
    return data.map((d) => {
      const entity = new StandardEntity(d.id, d.name, 'label');
      entity.icon = `circle fas ${d.css}`;
      return entity;
    });
  }

  protected onSelectItem($event: StandardEntity) {
    if (!$event) {
      return;
    }
    if ($event.id === this.color()) {
      return;
    }
    this.color.set($event.id);
    try {
      const action = this.actions.find((a) => a.name === 'customize');
      (action as SwitchAction<boolean>).isActive = false;
      this.displayOptions.set(false);
    } catch (e) {
      console.error(e);
    }
  }
}
